import { useEffect, useMemo, useState } from "react";
import { HelperService } from "../../../services/helpers/helper.service";
import UiTable from "../../../components/table/Table.component";
import { StoredCriteriaService } from "../../../services/session/stored-criteria.service";
import { FieldMessageSeverity } from "../../../models/components/field-message.model";
import {
	TableColumn,
	TableColumnTemplate,
} from "../../../models/components/table/table-column.model";
import { TableMessages } from "../../../models/components/table/table-message.model";
import { TableFilterType } from "../../../models/components/table/table-filter.model";
import { getSessionStorageUser } from "../../../services/session/auth.service";
import { BackendPagination } from "../../../models/pagination.model";
import { TaskRow } from "../../../models/pages/task-row.model";
import { AlertSeverity } from "../../../models/entities/alert.model";
import { AlertCategory } from "../../../models/entities/alert-category.model";
import { ListOption } from "../../../models/misc.model";
import { FilterService } from "../../../services/filter/filter.service";
import { Formatter } from "../../../services/formatter/formatter.service";
import { eParticipantStatus } from "../../../models/entities/participant.model";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useTasks } from "../../../hooks/useTasks";
import { useTasksCount } from "../../../hooks/useTasksCount";
import { useTasksCountsByCategory } from "../../../hooks/useTasksCountsByCategory";
import { useAlertCategories } from "../../../hooks/useAlertCategories";
import { useUpdateTask } from "../../../hooks/useUpdateTask";
import { useHideResolvedAlert } from "../../../hooks/useHideResolvedAlert";
import { mergeCategoryIntoFilters } from "../../../utils/alert-category-filters";
import { classNames } from "primereact/utils";
import { Tooltip } from "primereact/tooltip";
import { useProvidersTwilio } from "../../../hooks/useProvidersTwilio";
import { TableTemplateButtonsEvent } from "../../../models/components/table/table-template.model";
import { DataTableSortOrderType } from "../../../models/components/table/table-pagination.model";
import { PageProps } from "../../../models/routing.model";
import { useInstitutionsDetailed } from "../../../hooks/useInstitutionsDetailed";
import { Roles } from "../../../models/roles.model";
import UiModalAlert from "../../../components/modal-alert/ModalAlert.component";
import { useActiveProvidersInfo } from "../../../hooks/useActiveProvidersInfo";
import { Task } from "../../../models/entities/task.model";
import {
	getAlertReasonFilterOptions,
	renderAlertDetailsCell,
	renderAlertReasonCell,
} from "../../../utils/alert-details";

// Always-on background filters
const CATEGORY_FILTER = {
	field: "category",
	type: TableFilterType.SELECT_MULTIPLE,
	value: ["alert"],
};
const UNRESOLVED_FILTER = {
	field: "resolved",
	type: TableFilterType.SELECT_MULTIPLE,
	value: ["false"],
};

const ParticipantsAlerts = (props: PageProps) => {
	const { t } = useTranslation("common");
	const setColumns = () => {
		const columns: TableColumn[] = [
			{
				field: "kannactId",
				title: "PARTICIPANTS.ALERTS.TABLE.COLUMNS.kannactId",
				template: TableColumnTemplate.KANNACT_ID,
			},
			{
				field: "participantName",
				sortingField: "first_name,last_name",
				title: "PARTICIPANTS.ALERTS.TABLE.COLUMNS.name",
				sortable: true,
			},
			{
				field: "taskDate",
				title: "PARTICIPANTS.ALERTS.TABLE.COLUMNS.date",
				template: TableColumnTemplate.DATE,
				sortable: true,
			},
			{
				field: "reason",
				title: "PARTICIPANTS.ALERTS.TABLE.COLUMNS.reason",
				sortable: true,
				template: TableColumnTemplate.CUSTOM,
				templateOptions: {
					customTemplate: (rowData: TaskRow) => renderAlertReasonCell(t, rowData),
				},
			},
			{
				field: "details",
				title: "PARTICIPANTS.ALERTS.TABLE.COLUMNS.details",
				sortable: true,
				template: TableColumnTemplate.CUSTOM,
				templateOptions: {
					customTemplate: (rowData: TaskRow) => renderAlertDetailsCell(t, rowData),
				},
			},
			{
				field: "priorityTag",
				title: "PARTICIPANTS.ALERTS.TABLE.COLUMNS.severity",
				sortable: true,
				template: TableColumnTemplate.TAG,
			},
			{
				field: "reachouts",
				title: "PARTICIPANTS.ALERTS.TABLE.COLUMNS.reachouts",
				sortable: true,
			},
			{
				field: "lastAttempt",
				title: "PARTICIPANTS.ALERTS.TABLE.COLUMNS.lastAttempt",
				template: TableColumnTemplate.DATE,
				sortable: true,
			},
			{
				field: "actionsTemplate",
				template: TableColumnTemplate.BUTTONS,
				sortable: false,
				alignment: "right",
			},
		];

		const assignedToColumn = {
			field: "assignee",
			title: "PARTICIPANTS.ALERTS.TABLE.COLUMNS.assignedTo",
			template: TableColumnTemplate.COACH,
			sortable: true,
		};
		columns.splice(2, 0, assignedToColumn);

		return columns;
	};

	const { data: alertCategories = [] } = useAlertCategories();
	const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);

	useEffect(() => {
		if (alertCategories.length > 0 && selectedCategoryId === null) {
			setSelectedCategoryId(alertCategories[0].id);
		}
	}, [alertCategories, selectedCategoryId]);

	const [filters, setFilters] = useState(
		StoredCriteriaService.getFilters(StoredCriteriaService.KEYS["participants-alerts"]) ?? {
			filters: [
				{
					field: "status",
					type: TableFilterType.SELECT_MULTIPLE,
					value: [],
					options: {
						props: {
							label: "PARTICIPANTS.ALERTS.TABLE.FILTER_PARTICIPANT_STATUS",
							options: [
								{
									id: eParticipantStatus.PROSPECTIVE,
									label: "ENUMS.PARTICIPANT_STATUS.PROSPECTIVE",
								},
								{
									id: eParticipantStatus.ACTIVE,
									label: "ENUMS.PARTICIPANT_STATUS.ACTIVE",
								},
								{
									id: eParticipantStatus.DNC,
									label: "ENUMS.PARTICIPANT_STATUS.DNC",
								},
								{
									id: eParticipantStatus.INACTIVE,
									label: "ENUMS.PARTICIPANT_STATUS.INACTIVE",
								},
								{
									id: eParticipantStatus.SNOOZED,
									label: "ENUMS.PARTICIPANT_STATUS.SNOOZED",
								},
								{
									id: eParticipantStatus.UNREACHABLE,
									label: "ENUMS.PARTICIPANT_STATUS.UNREACHABLE",
								},
								{ id: "null", label: "ENUMS.PARTICIPANT_STATUS.NOT_COMPUTED" },
							],
						},
					},
				},
				{
					field: "severity",
					type: TableFilterType.SELECT_MULTIPLE,
					value: [],
					options: {
						props: {
							label: "PARTICIPANTS.ALERTS.TABLE.FILTER_TITLE_SEVERITY",
							options: Object.keys(AlertSeverity).map(
								(s) => new ListOption({ id: s, label: `ENUMS.ALERT_SEVERITY.${s}` })
							),
						},
					},
				},
				{
					field: "reason",
					type: TableFilterType.SELECT_MULTIPLE,
					value: [],
					options: {
						props: {
							label: "PARTICIPANTS.ALERTS.TABLE.FILTER_TITLE_REASON",
							options: getAlertReasonFilterOptions().map((o) => new ListOption(o)),
						},
					},
				},
				{
					field: "assignee",
					type: TableFilterType.SELECT_MULTIPLE,
					value: [],
					options: {
						props: {
							label: "PARTICIPANTS.ALERTS.TABLE.FILTER_TITLE_ASSIGNED_TO",
							options: [],
							filter: true,
						},
					},
				},
				{
					field: "is_test",
					type: TableFilterType.SELECT_MULTIPLE,
					value: ["false"],
					options: {
						columnClass: "col-12 col-md-2 col-lg-2 col-xl-1",
						props: {
							label: "PARTICIPANTS.DASHBOARD.FILTERS.IS_TEST_TITLE",
							options: [
								{ id: "true", label: "ENUMS.YES" },
								{ id: "false", label: "ENUMS.NO" },
							],
						},
					},
				},
				{
					field: "taskDate",
					type: TableFilterType.DATE_RANGE,
					options: {
						columnClass: "col-12 col-md-3 col-lg-2",
						props: {
							label: "PARTICIPANTS.ALERTS.TABLE.FILTER_TITLE_DATE_ON",
						},
					},
				},
			],
		}
	);

	const [pagination, setPagination] = useState(
		StoredCriteriaService.getPagination(StoredCriteriaService.KEYS["participants-alerts"]) ?? {
			first: 0,
			rows: 10,
			page: 0,
			sortField: "taskDate",
			sortOrder: -1 as DataTableSortOrderType,
			search: undefined,
			filters: filters.filters,
		}
	);

	const selectedCategory = alertCategories.find((c) => c.id === selectedCategoryId) ?? null;

	const buildBackendPagination = (
		pag: typeof pagination,
		category: AlertCategory | null
	): BackendPagination => {
		const paginationData = {
			...pag,
			sortField: HelperService.compoundSortField(setColumns(), pag),
		};

		let baseFilters = pag.filters || [];

		if (!HelperService.isAdminViewMode()) {
			baseFilters = [
				...baseFilters,
				{
					field: "assignee",
					type: TableFilterType.SELECT_MULTIPLE,
					value: [`${getSessionStorageUser().id}`],
				},
			];
		}

		// Always filter by category=alert and unresolved
		baseFilters = [...baseFilters, CATEGORY_FILTER, UNRESOLVED_FILTER];

		let filtersWithCategory = mergeCategoryIntoFilters(baseFilters, category);

		if (filtersWithCategory && FilterService.hasSelectedFilters(filtersWithCategory)) {
			filtersWithCategory = filtersWithCategory.map((item) => {
				if (item.field === "taskDate" && item.value) {
					const minDate = item.value[0]
						? Formatter.dateUTCWithoutTime(item.value[0] as Date)?.toISOString()
						: undefined;
					const maxDate = item.value[1]
						? Formatter.dateUTCWithoutTime(item.value[1] as Date)?.toISOString()
						: minDate;

					const maxDateISO = maxDate
						? (() => {
								const d = new Date(maxDate);
								d.setUTCHours(23, 59, 59, 999);
								return d.toISOString();
							})()
						: undefined;
					return { ...item, value: [minDate, maxDateISO] };
				}
				return item;
			});
		}

		paginationData.filters = filtersWithCategory;
		return new BackendPagination(paginationData);
	};

	const backendPagination = useMemo(
		() => buildBackendPagination(pagination, selectedCategory),
		[pagination, selectedCategory]
	);

	const getBackendPaginationForCategory = useMemo(() => {
		return (category: AlertCategory) => buildBackendPagination(pagination, category);
	}, [pagination]);

	const countsByCategory = useTasksCountsByCategory(
		alertCategories,
		getBackendPaginationForCategory
	);

	const queryClient = useQueryClient();
	const { data: tasks = [], isLoading, error } = useTasks(backendPagination);
	const { data: count = 0 } = useTasksCount(backendPagination);
	const updateMutation = useUpdateTask();
	const hideResolvedAlert = useHideResolvedAlert();
	const { data: providers } = useProvidersTwilio();
	const { data: institutions } = useInstitutionsDetailed();
	const { data: coachesList } = useActiveProvidersInfo();

	const [alertModal, setAlertModal] = useState<{
		entity: Task | null;
		isVisible: boolean;
	}>({ entity: null, isVisible: false });

	const editModal = (e: TableTemplateButtonsEvent) => {
		setAlertModal({
			entity: (e.row as TaskRow).entity as Task,
			isVisible: true,
		});
	};

	const cancelAlertModal = () => {
		setAlertModal((prev) => ({ ...prev, isVisible: false }));
	};

	const completeAlertModal = (refresh: boolean) => {
		setAlertModal((prev) => ({ ...prev, isVisible: false }));
		if (refresh) {
			queryClient.invalidateQueries({ queryKey: ["participants-tasks"] });
			queryClient.invalidateQueries({ queryKey: ["participants-tasks-count"] });
		}
	};

	const onCategoryTabSelect = (categoryId: string) => {
		setSelectedCategoryId(categoryId);
		setPagination((prev) => {
			const next = { ...prev, page: 0, first: 0 };
			StoredCriteriaService.setPagination(
				StoredCriteriaService.KEYS["participants-alerts"],
				next
			);
			return next;
		});
	};

	const alertsCustomHeader =
		alertCategories.length > 0 ? (
			<div className="p-tabview">
				<div className="p-tabview-nav">
					<ul>
						{alertCategories.map((cat) => (
							<li
								key={cat.id}
								className={classNames({
									"p-highlight": selectedCategoryId === cat.id,
								})}
							>
								{cat.description ? (
									<>
										<a
											id={`alerts-tab-${cat.id}`}
											className="p-tabview-nav-link"
											role="tab"
											href="#"
											onClick={(e) => {
												e.preventDefault();
												onCategoryTabSelect(cat.id);
											}}
										>
											{cat.name} ({countsByCategory[cat.id] ?? 0})
										</a>
										<Tooltip
											target={`#alerts-tab-${cat.id}`}
											content={cat.description}
											position="top"
										/>
									</>
								) : (
									<a
										className="p-tabview-nav-link"
										role="tab"
										href="#"
										onClick={(e) => {
											e.preventDefault();
											onCategoryTabSelect(cat.id);
										}}
									>
										{cat.name} ({countsByCategory[cat.id] ?? 0})
									</a>
								)}
							</li>
						))}
					</ul>
				</div>
			</div>
		) : null;

	const message = useMemo(() => {
		if (isLoading) {
			return {
				severity: FieldMessageSeverity.LOADING,
				label: new TableMessages().loading,
			};
		}

		if (error) {
			return {
				severity: FieldMessageSeverity.DANGER,
				label: new TableMessages().error,
			};
		}

		if (tasks.length === 0) {
			return {
				severity: FieldMessageSeverity.INFO,
				label: new TableMessages().empty,
			};
		}

		return undefined;
	}, [isLoading, error, tasks.length]);

	/**
	 * Optimistic resolve (KAN-2553 follow-up, same as Home's Critical alerts):
	 * the row, the table total and the category badge drop BEFORE the PATCH goes
	 * out, and come back if it fails. `useUpdateTask` refetches on success. If
	 * that was the last row of the last page, the paginator sees the lower total
	 * and steps back a page on its own (PrimeReact Paginator).
	 */
	const resolve = async (e: TableTemplateButtonsEvent) => {
		const task = (e.row as TaskRow).entity as Task;
		const restore = await hideResolvedAlert(task.id);
		updateMutation.mutate(
			{
				id: task.id,
				body: {
					resolved: true,
					participantId: task.participantId,
					resolutionSource: "MANUAL",
					resolvedByUserId: getSessionStorageUser().id,
				},
			},
			{ onError: () => restore() }
		);
	};

	useEffect(() => {
		if (
			HelperService.isAdminViewMode() &&
			!StoredCriteriaService.getFilters(StoredCriteriaService.KEYS["participants-alerts"]) &&
			providers &&
			institutions
		) {
			const institutionOptions = institutions.map(
				(x) => new ListOption({ id: x.institutionId, label: x.institutionName })
			);

			let updatedFilters = [...filters.filters];
			if (institutions?.length > 0 && !Roles.isProvider()) {
				const tmpInstitutionFilter = {
					field: "institutionId",
					type: TableFilterType.SELECT_MULTIPLE,
					value: [],
					options: {
						props: {
							label: "PARTICIPANTS.DASHBOARD.FILTERS.INSTITUTION_TITLE",
							options: institutionOptions,
							filter: true,
						},
					},
				};
				updatedFilters = [...updatedFilters, tmpInstitutionFilter];
			}
			setFilters({ ...filters, filters: updatedFilters });
			StoredCriteriaService.setFilters(StoredCriteriaService.KEYS["participants-alerts"], {
				...filters,
				filters: updatedFilters,
			});
		}
	}, [providers, institutions]);

	// Populate assignee filter options from coaches
	useEffect(() => {
		if (!coachesList?.length) return;
		const assigneeFilter = filters.filters.find((f) => f.field === "assignee");
		if (!assigneeFilter?.options?.props) return;
		const updatedAssigneeFilter = {
			...assigneeFilter,
			options: {
				...assigneeFilter.options,
				props: {
					...assigneeFilter.options.props,
					options: coachesList.map(
						(item) =>
							new ListOption({
								id: item.coachId,
								label: Formatter.fullName(item.firstName, item.lastName),
							})
					),
				},
			},
		};
		setFilters({
			...filters,
			filters: filters.filters.map((filter) =>
				filter.field === "assignee" ? updatedAssigneeFilter : filter
			),
		});
	}, [coachesList]);

	return (
		<>
			<UiTable
				useAsCard
				dataKey="id"
				title={
					HelperService.isAdminViewMode()
						? "PARTICIPANTS.ALERTS.TABLE.TITLE_ADMIN"
						: "PARTICIPANTS.ALERTS.TABLE.TITLE_COACH"
				}
				secondaryHeader={alertsCustomHeader}
				columns={setColumns()}
				value={tasks.map((row) => new TaskRow(row, editModal, resolve))}
				message={message}
				isServerPaginated
				pagination={pagination}
				paginationFn={(event) => {
					const newPagination = { ...pagination, ...event };
					setPagination(newPagination);
					StoredCriteriaService.setPagination(
						StoredCriteriaService.KEYS["participants-alerts"],
						newPagination
					);
				}}
				totalRecords={count}
				search={{
					value: pagination.search,
					fn: (value) => {
						const newPagination = {
							...pagination,
							search: value?.trim() || undefined,
							page: 0,
							first: 0,
						};
						setPagination(newPagination);
						StoredCriteriaService.setPagination(
							StoredCriteriaService.KEYS["participants-alerts"],
							newPagination
						);
					},
				}}
				filter={filters}
				filterFn={(formData) => {
					const newFilters = FilterService.updateFiltersValues(filters.filters, formData);
					const newPagination = { ...pagination, filters: newFilters, page: 0, first: 0 };
					setFilters({ ...filters, filters: newFilters });
					setPagination(newPagination);
					StoredCriteriaService.set(
						StoredCriteriaService.KEYS["participants-alerts"],
						newPagination,
						{ ...filters, filters: newFilters }
					);
				}}
			/>
			{alertModal.isVisible && alertModal.entity && (
				<UiModalAlert
					entity={alertModal.entity}
					isVisible={alertModal.isVisible}
					onCancel={cancelAlertModal}
					onComplete={completeAlertModal}
				/>
			)}
		</>
	);
};

export default ParticipantsAlerts;
